import { SideNavDataSource } from './sidebar.model';

type MenuItemMatch = { id?: string; name: string };

const PUBLIC_ROUTES = ['/', '/home', '/login', '/dashboard', '/unauthorized', '/404'];

const MODULE_PREFIXES = [
  '/material-management',
  '/financial-module',
  '/logistic',
  '/dashboard',
  '/ai',
];

const normalizeRouterLink = (link: unknown): string => {
  if (!link) {
    return '';
  }
  const joined = Array.isArray(link) ? link.join('/') : String(link);
  return '/' + joined.replace(/^\/+/, '').replace(/\/+$/, '');
};

// Routes can be plant-prefixed (`:plant/material-management/...`) while sidebar links are not.
// Strip a leading `/{plant}` segment when followed by a known module path.
const stripPlantPrefix = (path: string): string => {
  const modulePattern = MODULE_PREFIXES.map((p): string => p.replace('/', '')).join('|');
  const re = new RegExp(`^/[^/]+(/(?:${modulePattern})(?=/|$))`);
  return path.replace(re, '$1');
};

const findMenuItemByUrl = (url: string): MenuItemMatch | null => {
  if (!url) {
    return null;
  }
  const pathOnly = url.split('?')[0].split('#')[0];
  const normalized = stripPlantPrefix(pathOnly);
  const menu = new SideNavDataSource().sideNavDataSource;
  for (const group of menu) {
    if (!group.children) {
      continue;
    }
    for (const child of group.children) {
      const link = normalizeRouterLink(child.options && child.options.routerLink);
      if (!link) {
        continue;
      }
      if (normalized === link || normalized.startsWith(link + '/')) {
        return { id: child.id, name: child.name };
      }
    }
  }
  return null;
};

export const isUrlAccessible = (url: string, menuAccessRightList: string[]): boolean => {
  if (!url) {
    return true;
  }
  const pathOnly = url.split('?')[0].split('#')[0];
  const normalized = stripPlantPrefix(pathOnly);
  if (PUBLIC_ROUTES.indexOf(normalized) !== -1) {
    return true;
  }

  const item = findMenuItemByUrl(url);
  // Route not in sidebar — leave alone, don't redirect.
  if (!item) {
    return true;
  }

  const rights = menuAccessRightList || [];
  return rights.indexOf(item.id || item.name) !== -1;
};

// Landing fallback chain: /dashboard if the user has any widgets, otherwise the first
// accessible sidebar item, otherwise /unauthorized for users with no rights at all.
export const resolveLandingUrl = (
  menuAccessRightList: string[],
  widgetAccessRightList?: string[],
): string => {
  if (!menuAccessRightList || !menuAccessRightList.length) return '/unauthorized';
  if (widgetAccessRightList && widgetAccessRightList.length > 0) return '/dashboard';

  const menu = new SideNavDataSource().sideNavDataSource;
  for (const group of menu) {
    if (!group.children) continue;
    for (const child of group.children) {
      const key = child.id || child.name;
      if (menuAccessRightList.indexOf(key) === -1) continue;
      const link = normalizeRouterLink(child.options && child.options.routerLink);
      if (link) return link;
    }
  }
  return '/dashboard';
};
